/**
 * EmptyStatePresets - Predefined Empty State Configurations
 *
 * Predefined empty state components for common scenarios.
 * Each preset includes appropriate icon, title, message, and action configuration.
 *
 * Tone: Factual, calm, no hype. See ADR-UX-005.
 * - Titles are factual nouns (no "yet")
 * - Messages explain what belongs here
 * - One action max, natural next step
 */

import EmptyState from './EmptyState';
import { MessageSquare, Users, Calendar, UserPlus, MapPin, CheckCircle } from 'lucide-react';

// Common preset props interface
interface PresetProps {
  onAction?: () => void;
  className?: string;
  variant?: 'default' | 'compact' | 'inline';
}

/**
 * No Messages Empty State
 * Used in: ChatArea, messaging contexts
 */
export function NoMessagesEmptyState({ onAction, className, variant = 'default' }: PresetProps) {
  return (
    <EmptyState
      variant={variant}
      icon={<MessageSquare />}
      title="No messages"
      message="Messages will appear here when someone starts a conversation."
      actionLabel="Send message"
      onAction={onAction}
      className={className}
    />
  );
}

/**
 * No Clubs Empty State
 * Used in: Club discovery, user club lists
 */
export function NoClubsEmptyState({ onAction, className, variant = 'default' }: PresetProps) {
  return (
    <EmptyState
      variant={variant}
      icon={<Users />}
      title="No clubs"
      message="Join a club to see events and connect with members."
      actionLabel="Browse clubs"
      onAction={onAction}
      className={className}
    />
  );
}

/**
 * No Events Empty State
 * Used in: Event listings, calendars, event feeds
 */
export function NoEventsEmptyState({ onAction, className, variant = 'default' }: PresetProps) {
  return (
    <EmptyState
      variant={variant}
      icon={<Calendar />}
      title="No events scheduled"
      message="When events are scheduled, they'll appear here."
      actionLabel="Create event"
      onAction={onAction}
      className={className}
    />
  );
}

/**
 * No Members Empty State
 * Used in: Club member lists, member directories
 */
export function NoMembersEmptyState({ onAction, className, variant = 'default' }: PresetProps) {
  return (
    <EmptyState
      variant={variant}
      icon={<UserPlus />}
      title="No members"
      message="Members will appear here after they join this club."
      actionLabel="Invite members"
      onAction={onAction}
      className={className}
    />
  );
}

/**
 * No Upcoming Events Empty State
 * Used in: Personal dashboard, activity feed, home screen
 */
export function NoUpcomingMovesEmptyState({ onAction, className, variant = 'default' }: PresetProps) {
  return (
    <EmptyState
      variant={variant}
      icon={<MapPin />}
      title="No upcoming events"
      message="RSVP to events to see them here."
      actionLabel="Find events"
      onAction={onAction}
      className={className}
    />
  );
}

/**
 * No Check-Ins Empty State
 * Used in: Event participation tracking, attendance lists
 */
export function NoCheckInsEmptyState({ onAction, className, variant = 'default' }: PresetProps) {
  return (
    <EmptyState
      variant={variant}
      icon={<CheckCircle />}
      title="No check-ins"
      message="Check-ins will appear here after events."
      actionLabel="View events"
      onAction={onAction}
      className={className}
    />
  );
}

/**
 * Generic empty state for when presets don't fit
 * Maintains backward compatibility with existing EmptyState usage
 */
export function CustomEmptyState({
  icon,
  title,
  message,
  actionLabel,
  onAction,
  className,
  variant = 'default'
}: {
  icon: React.ReactNode;
  title: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
  className?: string;
  variant?: 'default' | 'compact' | 'inline';
}) {
  return (
    <EmptyState
      variant={variant}
      icon={icon}
      title={title}
      message={message}
      actionLabel={actionLabel}
      onAction={onAction}
      className={className}
    />
  );
}

/**
 * Preset selector for easy switching between common empty states
 */
export type EmptyStatePreset =
  | 'no-messages'
  | 'no-clubs'
  | 'no-events'
  | 'no-members'
  | 'no-upcoming-moves'
  | 'no-check-ins';

export function EmptyStateFromPreset({
  preset,
  onAction,
  className,
  variant = 'default'
}: {
  preset: EmptyStatePreset;
  onAction?: () => void;
  className?: string;
  variant?: 'default' | 'compact' | 'inline';
}) {
  switch (preset) {
    case 'no-messages':
      return <NoMessagesEmptyState onAction={onAction} className={className} variant={variant} />;
    case 'no-clubs':
      return <NoClubsEmptyState onAction={onAction} className={className} variant={variant} />;
    case 'no-events':
      return <NoEventsEmptyState onAction={onAction} className={className} variant={variant} />;
    case 'no-members':
      return <NoMembersEmptyState onAction={onAction} className={className} variant={variant} />;
    case 'no-upcoming-moves':
      return <NoUpcomingMovesEmptyState onAction={onAction} className={className} variant={variant} />;
    case 'no-check-ins':
      return <NoCheckInsEmptyState onAction={onAction} className={className} variant={variant} />;
    default:
      return null;
  }
}
